import React from 'react';

import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { Accordion, AccordionDetails, AccordionSummary, Box, Typography } from '@mui/material';

import type { OpportunityRoomAsset } from '~/hooks/apis/offers/useGetOffer';

import AssetFieldGrid from './AssetFieldGrid';
import { formatCreatedOrModifiedBy, formatSyncedDate, salesforceRecordUrl } from './utils';

interface Props {
  hideName?: boolean;
  hideSalesforceLinks?: boolean;
  roomAsset: OpportunityRoomAsset;
}

export default function RoomAssetDetailPanel({ hideName, hideSalesforceLinks, roomAsset }: Props) {
  return (
    <Box>
      {!hideName && (
        <Typography variant="h6" sx={{ mb: 2, px: 2 }}>
          {roomAsset.name}
        </Typography>
      )}

      <Box sx={{ mb: 3, px: 2 }}>
        <AssetFieldGrid
          left={[
            { label: 'Asset Name', value: roomAsset.name },
            { label: 'Asset Currency', value: roomAsset.currencyisocode },
            { label: 'Last Validated On', value: formatSyncedDate(roomAsset.last_validated_on__c) },
            { label: 'Maximum Occupancy (Number)', value: roomAsset.maximum_occupancy_number__c },
            { label: 'Included Number of Adults', value: roomAsset.included_number_of_adults__c },
            { label: 'Included Number Of Children', value: roomAsset.included_number_of_children__c },
            { label: 'PRV', value: roomAsset.prv__c },
            { label: 'Max_Adults', value: roomAsset.max_adults__c },
            { label: 'Max Children', value: roomAsset.max_children__c },
            { label: 'Max Teenagers', value: roomAsset.max_teenagers__c },
            { label: 'Max Infants', value: roomAsset.max_infants__c },
          ]}
          right={[
            {
              label: 'Account',
              value: roomAsset.account__name,
              href: hideSalesforceLinks ? undefined : salesforceRecordUrl('Account', roomAsset.accountid),
            },
            { label: 'Asset Record Type', value: roomAsset.recordtype__name },
            { label: 'Type', value: roomAsset.type__c },
          ]}
        />
      </Box>

      <Accordion defaultExpanded disableGutters>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography variant="subtitle1">Room Specific Details</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <AssetFieldGrid
            left={[
              { label: 'Maximum Occupancy', value: roomAsset.max_occupancy__c },
              { label: 'Standard Bedding Configuration', value: roomAsset.standard_bedding_configuration__c },
              { label: 'Additional Bedding Type', value: roomAsset.additional_bedding_type__c },
              { label: 'Additional Bedding Information', value: roomAsset.additional_bedding_information__c },
              { label: 'Max Child Age', value: roomAsset.max_child_age__c },
              { label: 'Child Bedding', value: roomAsset.child_bedding__c },
              { label: 'Cot Provided?', value: roomAsset.cot_provided__c },
            ]}
            right={[
              { label: 'Rack Rate', value: roomAsset.rack_rate__c },
              { label: 'Number of Rooms Connecting', value: roomAsset.number_of_rooms_connecting__c },
              { label: 'Number of Rooms on site', value: roomAsset.number_of_rooms_on_site__c },
              { label: 'Room Size', value: roomAsset.room_size__c },
              { label: 'Room View', value: roomAsset.room_view__c },
              { label: 'Opportunity', value: roomAsset.opportunity__c },
              { label: 'Current Opp', value: roomAsset.current_opp__c },
              { label: 'Max Infant Age', value: roomAsset.max_infant_age__c },
            ]}
          />
        </AccordionDetails>
      </Accordion>

      <Accordion defaultExpanded disableGutters>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography variant="subtitle1">System Information</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <AssetFieldGrid
            left={[
              {
                label: 'Created By',
                value: formatCreatedOrModifiedBy(roomAsset.createdby__name, roomAsset.createddate),
              },
            ]}
            right={[
              {
                label: 'Last Modified By',
                value: formatCreatedOrModifiedBy(roomAsset.lastmodifiedby__name, roomAsset.lastmodifieddate),
              },
            ]}
          />
        </AccordionDetails>
      </Accordion>
    </Box>
  );
}
